import { BitmapLayer } from '@deck.gl/layers/typed';
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useAlertHook } from 'app/hooks/useAlertHook';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'app/hooks/useDebounce';
import { Bbox } from 'ais-facade-api/api';
import { useBboxValidation } from 'app/helpers';
import { useGetAnalyticsMapMutation } from 'app/api/facade';
import { useAppSelector } from 'app/store/store';

export const useAnalyticsLayer = (
  viewState: Record<string, any>,
  isAnalyticsMode: boolean,
  bbox: Bbox,
  setShowLoader: Dispatch<SetStateAction<boolean>>,
  setDisabledMode: Dispatch<SetStateAction<boolean>>,
  analyticsMode: string,
) => {
  if (analyticsMode === 'static') return null;
  const [getAnalyticsMap, { ...imageRequest }] = useGetAnalyticsMapMutation();
  const [analyticsImage, setAnalyticsImage] = useState<any>(null);
  const { addAlert } = useAlertHook();
  const { t } = useTranslation();
  const { width, height } = viewState;
  const [bboxValue, setBboxValue] = useState<string | Bbox>('');
  const debouncedBboxValue = useDebounce(bboxValue, 1000);
  const { isValidBbox } = useBboxValidation();

  useEffect(() => {
    setBboxValue(bbox);
    if (isAnalyticsMode) {
      setShowLoader(true);
    }
  }, [bbox]);

  useEffect(() => {
    setAnalyticsImage(null);
  }, [viewState]);

  useEffect(() => {
    if (
      viewState &&
      isAnalyticsMode &&
      bbox &&
      Object.keys(bbox).length !== 0 &&
      typeof height === 'number' &&
      typeof bbox?.first?.latitude === 'number'
    ) {
      if (isValidBbox(bbox)) {
        getImage();
      }
    }
  }, [isAnalyticsMode, debouncedBboxValue]);

  const periodFetch = useAppSelector((state) => state.facadeSlice.anlyticsFilter.period);
  const shipType = useAppSelector((state) => state.facadeSlice.anlyticsFilter.shipType);

  useEffect(() => {
    if (
      viewState &&
      isAnalyticsMode &&
      bbox &&
      Object.keys(bbox).length !== 0 &&
      typeof height === 'number' &&
      typeof bbox?.first?.latitude === 'number'
    ) {
      if (isValidBbox(bbox)) {
        getImage();
      }
    }
  }, [periodFetch, shipType]);

  const getImage = () => {
    setDisabledMode(true);
    setAnalyticsImage(null);
    setShowLoader(true);
    getAnalyticsMap({
      filters: {
        bbox: {
          first: {
            latitude: bbox.first.latitude,
            longitude: bbox.first.longitude,
          },
          second: {
            latitude: bbox.second.latitude,
            longitude: bbox.second.longitude,
          },
        },
        width: width,
        height: height,
        type: shipType,
        color: 'ALPHA_HEAT',
        period: periodFetch,
      },
    })
      .unwrap()
      .then((res: Blob) => {
        const reader = new FileReader();
        reader.readAsDataURL(res);
        reader.onloadend = () => {
          setAnalyticsImage(reader.result);
        };
        setDisabledMode(false);
        setShowLoader(false);
      })
      .catch(() => {
        setDisabledMode(false);
        setShowLoader(false);
        addAlert({
          data: {
            title: 'Error',
            detail: 'Error',
            messages: [{ description: t('ResourceIsTemporarilyUnavailable'), type: 'error' }],
          },
        });
      });
  };

  return analyticsImage
    ? new BitmapLayer({
      id: 'analytics-layer',
      visible: isAnalyticsMode && !imageRequest.isLoading,
      bounds: [bbox.first.longitude, bbox.first.latitude, bbox.second.longitude, bbox.second.latitude],
      image: analyticsImage,
    })
    : null;
};
